/**
 * "תמחור" tab — used inside CustomerDetailsModal and in the price-list page modal.
 */
import { missingPricingFields } from "../../utils/pricing-validation";
import { completeDiameterRows } from "../../utils/pricing-diameters";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { focusMissingPrice } from "./pricing-form-feedback";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { fetchCustomerPricing, fetchSupplierPricing, saveCustomerPricing } from "Redux/middlwares/supplierPricing";
import { CustomerPricing } from "../../utils/supplier-pricing-engine";
import { PBtn, PField, PNumber, PRadioCard, PSelect, fmt0, toNum } from "./ui";

export default function CustomerPricingTab({ customerId, onDone }: { customerId: number; onDone?: () => void }) {
  const { t } = useTranslation(["supplierPricing"]);
  const dispatch = useAppDispatch();
  const stored = useAppSelector((s) => s.supplierPricing.customerPricing[customerId]);
  const priceList = useAppSelector((s) => s.supplierPricing.saved);
  const formRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);
  const [v, setV] = useState<CustomerPricing | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let active = true;
    setV(null); setLoading(true); setLoadError(false);
    Promise.resolve(dispatch(fetchCustomerPricing(customerId)))
      .catch(() => { if (active) setLoadError(true); })
      .finally(() => { if (active) setLoading(false); });
    if (!priceList) dispatch(fetchSupplierPricing());
    return () => { active = false; };
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (stored && !loading && !loadError) setV(JSON.parse(JSON.stringify(stored)));
  }, [stored, loading, loadError]);

  if (loadError) return <p role="alert">{t("order.customerLoadFailed")}</p>;

  if (!v) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  const set = (patch: Partial<CustomerPricing>) => setV({ ...v, ...patch });
  const generalPrice = (d: number) => priceList?.diameterPrices.find((r) => r.diameter === d)?.ribbed ?? null;
  const diameterRows = completeDiameterRows(v.diameterOverrides, diameter => ({ diameter, pricePerTon: null }), priceList?.diameterPrices.map(row => row.diameter) || []);
  const custom = v.mode === "custom";
  const missing = !custom ? [] : priceList ? missingPricingFields(priceList, v, key => t(key)) : ["common.loading"];
  const invalid = missing.length > 0 || custom && (v.diameterOverrides.some(o => o.pricePerTon != null && o.pricePerTon < 0) || [v.cutPerTon, v.bendPerTon, v.unitPrice].some(rate => rate != null && rate < 0));

  const save = async () => {
    if (invalid) {
      toast.error(t("missingFields"));
      focusMissingPrice(formRef.current);
      return;
    }
    setSaving(true);
    try {
      await dispatch(saveCustomerPricing(customerId, { ...v, diameterOverrides: diameterRows }, onDone));
    } finally {
      setSaving(false);
    }
  };
  const fillMissing = () => {
    const first = diameterRows.find(row => row.pricePerTon != null && row.pricePerTon >= 0);
    if (!first) { toast.error(t("diameter.enterFirst")); return; }
    set({ diameterOverrides: diameterRows.map(row => row.pricePerTon == null ? { ...row, pricePerTon: first.pricePerTon } : row) });
  };

  return (
    <div ref={formRef} className="eb-pr eb-pr-col">
      <div className="eb-pr-row">
        <PRadioCard on={custom} title={t("customerTab.customMode")} desc={t("customerTab.customModeDesc")} onClick={() => set({ mode: "custom" })} />
        <PRadioCard on={!custom} title={t("customerTab.defaultMode")} desc={t("customerTab.defaultModeDesc")} onClick={() => set({ mode: "default" })} />
      </div>

      {custom && (
        <>
          <div className="eb-pr-row">
            <PField label={t("customerTab.discount")} style={{ flex: 1 }}>
              <PNumber value={v.discountPercent} onChange={(x) => set({ discountPercent: Number(x || 0) })} suffix={t("units.percent")} />
            </PField>
            <PField label={t("customerTab.waste")} style={{ flex: 1 }}>
              <PNumber value={v.wastePercent} onChange={(x) => set({ wastePercent: toNum(x) })} suffix={t("units.percent")} />
            </PField>
            <PField label={t("customerTab.paymentTerms")} style={{ flex: 1 }}>
              <PSelect
                value={v.paymentTerms}
                onChange={(x) => set({ paymentTerms: x })}
                options={["cash", "net30", "net60", "net90"].map((k) => ({ value: k, label: t(`customerTab.terms.${k}`) }))}
              />
            </PField>
          </div>

          <div className="eb-pr-row">
            <PField label={t("customerTab.transport")} style={{ flex: 1 }}>
              <PSelect
                value={v.transportMode}
                onChange={(x) => set({ transportMode: x as CustomerPricing["transportMode"] })}
                options={["pricelist", "custom"].map((k) => ({ value: k, label: t(`customerTab.transportModes.${k}`) }))}
              />
            </PField>
            <PField label={t("transport.price")} style={{ flex: 1 }}>
              <PNumber ariaLabel={t("transport.price")} warn={v.transportMode === "custom" && (v.transportPrice == null || v.transportPrice < 0)} value={v.transportPrice} disabled={v.transportMode !== "custom"} onChange={(x) => set({ transportPrice: toNum(x) })} suffix={t("units.ils")} />
            </PField>
            <PField label={t("transport.freeAbove")} style={{ flex: 1 }}>
              <PNumber
                value={v.transportFreeAboveTons}
                disabled={v.transportMode !== "custom"}
                onChange={(x) => set({ transportFreeAboveTons: toNum(x) })}
                suffix={t("units.ton")}
              />
            </PField>
          </div>

          <div className="eb-pr-row" data-customer-processing>
            {(["bendPerTon", "cutPerTon", "unitPrice"] as const).map((field) => {
              const label = t(`processing.${field === "bendPerTon" ? "bend" : field === "cutPerTon" ? "cut" : "unitPrice"}`);
              return <PField key={field} label={label} style={{ flex: 1 }}>
                <PNumber value={v[field]} warn={(v[field] ?? priceList?.[field]) == null || (v[field] ?? priceList?.[field] ?? 0) < 0} ariaLabel={label} placeholder={t("customerTab.inheritProcessing")}
                  onChange={(x) => set({ [field]: toNum(x) })} suffix={t(field === "unitPrice" ? "units.perUnit" : "units.perTon")} />
              </PField>;
            })}
          </div>
          <p className="eb-pr-sub">{t("customerTab.processingNote")}</p>
          <div className="eb-pr-between">
            <b>{t("customerTab.overridesTitle")}</b>
          </div>
          <table className="eb-pr-table">
            <thead>
              <tr>
                <th>{t("diameter.col")}</th>
                <th>{t("customerTab.general")}</th>
                <th><div className="eb-pr-between"><span>{t("customerTab.forCustomer")}</span>
                  <button type="button" className="eb-pr-link" onClick={fillMissing}>{t("diameter.fillMissing")}</button>
                </div></th>
              </tr>
            </thead>
            <tbody>
              {diameterRows.map((o) => {
                const g = generalPrice(o.diameter);
                return (
                  <tr key={o.diameter}>
                    <td>
                      <b>{o.diameter}</b>
                    </td>
                    <td>{g !== null ? fmt0(g) : "—"}</td>
                    <td>
                      <PNumber
                        size="sm"
                        ariaLabel={`${o.diameter}`}
                        value={o.pricePerTon}
                        warn={o.pricePerTon != null ? o.pricePerTon < 0 : missing.some(key => key.startsWith(`Ø ${o.diameter} ·`))}
                        placeholder={t("customerTab.inheritProcessing")}
                        onChange={(x) => set({ diameterOverrides: diameterRows.map(r => r.diameter === o.diameter ? { ...r, pricePerTon: toNum(x) } : r) })}
                      />
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td className="eb-pr-muted">{t("customerTab.restRow")}</td>
                <td className="eb-pr-muted" colSpan={2}>
                  {v.discountPercent ? <bdi dir="ltr">-{v.discountPercent}%</bdi> : t("customerTab.defaultMode")}
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      <p className="eb-pr-sub">{t("customerTab.note")}</p>
      {missing.length > 0 && <p role="alert" className="eb-pr-warn">{t("missingFields")}</p>}
      {invalid && !missing.length && <p role="alert">{t("customerTab.requiredRates")}</p>}
      <div className="eb-pr-footer">
        {onDone && (
          <PBtn kind="secondary" onClick={onDone}>
            {t("customerTab.cancel")}
          </PBtn>
        )}
        <PBtn disabled={saving} onClick={save}>{t(saving ? "saving" : "customerTab.save")}</PBtn>
      </div>
    </div>
  );
}
